import type { CatalogMap } from './catalog';
import { findNode, hydrateIds, stripIds, type BuilderNode } from './useBuilderTree';

/**
 * Converting one element into another — a decline link into a filled button and back — is a
 * different problem from copying attributes across, because the two types spell the same idea
 * differently: a text's size is `size`, a button's is `font-size`.
 *
 * So each convertible type declares where it keeps each *meaning*, and conversion moves meanings
 * rather than attribute names. Anything the target has no place for is dropped, and anything the
 * target needs but the source never had comes from the target's own defaults.
 */
type Semantic = 'label' | 'font' | 'fontSize' | 'fontWeight' | 'color' | 'letterCase' | 'letterSpacing' | 'link';

const SEMANTICS: Record<string, Partial<Record<Semantic, string>>> = {
  'custom-text': {
    color: 'color',
    font: 'font',
    fontSize: 'size',
    fontWeight: 'weight',
    label: 'text',
    letterCase: 'text-transform',
    letterSpacing: 'letter-spacing',
    link: 'href',
  },
  // Both types spell the link the same, but it still has to be declared: conversion copies
  // meanings, so an attribute missing from this map is reset to the target's default.
  'ui-button': {
    color: 'color',
    font: 'font',
    fontSize: 'font-size',
    fontWeight: 'text-weight',
    label: 'text',
    letterCase: 'text-transform',
    letterSpacing: 'letter-spacing',
    link: 'href',
  },
};

/** `ui-button` chooses its click event from `action`, so the two must agree after a conversion. */
const ACTION_FOR_EVENT: Record<string, string> = {
  'protection-add': 'add',
  'protection-remove': 'remove',
};

const EVENT_FOR_ACTION: Record<string, string> = {
  add: 'protection-add',
  remove: 'protection-remove',
};

export function canConvert(catalog: CatalogMap, from: string, to: string): boolean {
  return !!catalog[from]?.convertsTo?.includes(to) && !!catalog[to];
}

/**
 * Rewrites `node` in place as `targetType`.
 *
 * `emits` is carried across verbatim, and that is the load-bearing part: the cart-widget runtime
 * derives the stable third-party id (`op-checkout-plus`, `op-continue-without-checkout-plus`) from
 * `emits`, not from the element type — so Loop, Rebuy and analytics selectors survive the swap.
 */
export function convertNode(
  nodes: BuilderNode[],
  id: string,
  targetType: string,
  catalog: CatalogMap,
): BuilderNode | null {
  const node = findNode(nodes, id);
  if (!node) return null;
  if (!canConvert(catalog, node.type, targetType)) return null;

  const target = catalog[targetType];
  const from = SEMANTICS[node.type] ?? {};
  const to = SEMANTICS[targetType] ?? {};
  const source = node.attributes ?? {};

  const attributes: Record<string, string | number | boolean> = { ...target.defaultAttributes };

  for (const key of Object.keys(to) as Semantic[]) {
    const fromAttr = from[key];
    const toAttr = to[key];
    if (!fromAttr || !toAttr) continue;

    const value = source[fromAttr];
    if (value !== undefined && value !== '') attributes[toAttr] = value;
  }

  // A target that takes no children cannot keep them; one that does keeps them as they were.
  const keepsChildren = target.canHaveChildren && !!node.children?.length;
  const children = keepsChildren ? hydrateIds(stripIds(node.children!)) : undefined;

  const event = node.emits?.find(e => ACTION_FOR_EVENT[e]);

  if (targetType === 'ui-button') {
    if (event) attributes.action = ACTION_FOR_EVENT[event];

    // A button with no `emits` is inert — the runtime has nothing to forward its click through.
    // Converting a text that never emitted anything would otherwise produce a dead button, so
    // wire it from whatever action the target defaults to.
    const fallback = EVENT_FOR_ACTION[String(attributes.action)];
    if (!event && fallback) node.emits = [fallback];
  } else {
    delete attributes.action;
  }

  node.type = targetType;
  node.attributes = attributes;

  if (children) node.children = children;
  else delete node.children;

  return node;
}

/** What a merchant loses by converting — surfaced before they commit to it. */
export function conversionLosses(node: BuilderNode, targetType: string, catalog: CatalogMap): string[] {
  const target = catalog[targetType];
  if (!target) return [];

  const losses: string[] = [];
  const from = SEMANTICS[node.type] ?? {};
  const to = SEMANTICS[targetType] ?? {};
  const source = node.attributes ?? {};

  // Meanings the source carries a value for that the target has nowhere to put.
  for (const key of Object.keys(from) as Semantic[]) {
    if (to[key]) continue;

    const value = source[from[key]!];
    if (value !== undefined && value !== '') losses.push(key);
  }

  // Everything the target can hold: its defaults plus anything it exposes a control for. `href`
  // has a control but no default, so defaults alone would miss it.
  const targetAttrs = new Set([
    ...Object.keys(target.defaultAttributes),
    ...target.controls.filter(c => c.target !== 'parent').map(c => c.attr),
  ]);
  const semanticAttrs = new Set(Object.values(from));

  // Iterate what is actually set rather than the schema, so a value the merchant typed into a
  // control with no default still counts.
  for (const attr of Object.keys(source)) {
    if (semanticAttrs.has(attr) || targetAttrs.has(attr)) continue;

    const value = source[attr];
    if (value !== undefined && value !== '' && value !== 'none' && value !== 'normal') losses.push(attr);
  }

  if (!target.canHaveChildren && node.children?.length) losses.push('nested elements');

  return [...new Set(losses)];
}
